/**
 * The two reads the calendar's robot lanes add, for a day or week window.
 *
 * Both are WINDOW reads, keyed by the window, so paging back and forth
 * re-uses what was read and nothing is fetched for a window nobody
 * opened. Neither replaces the live list: it is what the lanes draw for
 * anything it still holds.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchFireEvents, fetchFireMode, fetchTaskCompletions } from '../v1-client';
import type { FireEvent, SiteId, TaskCompletionRecord } from '../v1';

/** Biggest page the server serves. */
const PAGE = 200;
/** A week of fifteen robots at thirty tasks a day is ~3,150 rows: 16
 *  pages. Past this the read stops and says it is partial. */
const MAX_PAGES = 25;
/** A task that STARTED in the window may finish after it (an overnight
 *  clean): finishes are read this far past the window's end. */
export const COMPLETION_SLACK_MS = 6 * 60 * 60 * 1000;

export interface WindowCompletions {
  entries: TaskCompletionRecord[];
  /** False when the read stopped at `MAX_PAGES` with more to go. */
  complete: boolean;
}

/**
 * Finished tasks for `[fromMs, toMs)` from the durable record --
 * `task_completions`, by finish time from `fromMs` to `toMs` plus the
 * slack. Only when `needed`: the screen asks for it when the window
 * reaches back past what its live list still holds (the live store is
 * pruned after the site's live-retention days), and for no window in the
 * future.
 *
 * An api-server older than `after_ms` ignores it and keeps paging toward
 * the oldest row, so the walk also stops itself once a page ends below
 * the floor.
 */
export function useTaskCompletionsInWindow(
  siteId: string,
  window: { fromMs: number; toMs: number } | null,
  needed: boolean,
) {
  const fromMs = window?.fromMs ?? 0;
  const toMs = window?.toMs ?? 0;
  return useQuery<WindowCompletions>({
    queryKey: ['v1', siteId, 'task_completions', 'window', fromMs, toMs],
    enabled: Boolean(siteId) && window !== null && needed,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const entries: TaskCompletionRecord[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < MAX_PAGES; i += 1) {
        const page = await fetchTaskCompletions(siteId as SiteId, {
          beforeMs: toMs + COMPLETION_SLACK_MS,
          afterMs: fromMs,
          limit: PAGE,
          ...(cursor ? { cursor } : {}),
        });
        let belowFloor = false;
        for (const e of page.entries) {
          if (e.completed_at_ms < fromMs) belowFloor = true;
          else entries.push(e);
        }
        cursor = page.next_cursor ?? undefined;
        if (!cursor || belowFloor) return { entries, complete: true };
      }
      return { entries, complete: false };
    },
  });
}

export interface WindowFireEvents {
  events: FireEvent[];
  /** `history`: every event in the window. `current`: an api-server
   *  without the history route -- only the event open right now (if
   *  any) is known, and a past day's drills are not. */
  source: 'history' | 'current';
}

export function useFireEventsInWindow(
  siteId: string,
  window: { fromMs: number; toMs: number } | null,
) {
  const fromMs = window?.fromMs ?? 0;
  const toMs = window?.toMs ?? 0;
  return useQuery<WindowFireEvents>({
    queryKey: ['v1', siteId, 'fire-events', fromMs, toMs],
    enabled: Boolean(siteId) && window !== null,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const history = await fetchFireEvents(siteId as SiteId, fromMs, toMs);
      if (history !== null) return { events: history, source: 'history' };
      const state = await fetchFireMode(siteId as SiteId);
      const e = state.event;
      const end = e?.resolved_at_ms ?? Date.now();
      const inWindow = e && e.triggered_at_ms < toMs && end > fromMs;
      return { events: inWindow ? [e] : [], source: 'current' };
    },
  });
}
